import type { CategoryContent } from './row.js';

/** Visual Arts & Architecture, Music & Performing Arts, Film & Television. */

export const MUSIC: CategoryContent = {
  msc: [

    [
      'In figured bass, a lone "6" beneath a note indicates what?',
      [
        'A first-inversion chord, with a sixth above the bass in place of a fifth',
        'A six-four chord, sounding both the sixth and the fourth above the bass',
        'A suspension resolving downward onto the fifth',
        'The sixth degree of the prevailing scale as a pedal point',
      ],
      0,
      'Realising figured bass at sight was a working skill, not a theory exercise: the "6" tells you what to leave out as much as what to play.',
    ],
    [
      'In sonata form, what characterises the development?',
      [
        'Fragmentation and modulation of the exposition\'s material through unstable keys',
        'The first presentation of the second subject in the dominant',
        'A return to the tonic with both subjects restated',
        'A closing section that confirms the tonic through a series of repeated perfect cadences',
      ],
      0,
      'The other three describe the exposition, the recapitulation and the coda. Tonal instability is what makes the return feel like an arrival.',
    ],
    [
      'What is a hemiola?',
      [
        'A rhythmic displacement in which three beats occupy the time of two',
        'A melodic figure that inverts at the octave',
        'A cadence in which the bass rises by a step to the tonic rather than falling',
        'A drone sustained beneath a moving upper voice',
      ],
      0,
      'Ubiquitous in Baroque triple-time cadences and again in Brahms, where it is a structural rather than decorative device.',
    ],
    [
      'Ritornello form organises a movement how?',
      [
        'A recurring orchestral refrain alternating with contrasting solo episodes',
        'A single theme varied continuously over a repeating bass',
        'Two subjects presented, developed and recapitulated in the tonic',
        'A fugal exposition followed by episodes and a final stretto',
      ],
      0,
      'The Baroque concerto\'s standard plan, and the ancestor of the classical rondo - though the ritornello usually returns transposed and abbreviated rather than intact.',
    ],
    [
      'What does musica ficta refer to?',
      [
        'Accidentals supplied by performers in early music rather than written in the source',
        'A notational shorthand for repeated rhythmic patterns',
        'Music composed for instruments that no longer exist',
        'A theoretical scale used in teaching and speculative theory but not in actual composition',
      ],
      0,
      'Editors still argue about where it belongs, which is why two reputable editions of the same fifteenth-century piece can differ audibly.',
    ],
  ],
  phd: [
    [
      'A Neapolitan sixth chord is built on which scale degree?',
      ['The flattened second', 'The fourth', 'The flattened sixth', 'The leading tone'],
      0,
      'A major triad on the flattened supertonic, almost always in first inversion, which is where the "sixth" comes from.',
    ],
    [
      'What distinguishes a passacaglia from a chaconne?',
      [
        'The passacaglia is usually built on a repeating bass line, the chaconne on a repeating harmonic progression',
        'The passacaglia is always in duple metre',
        'The chaconne is a vocal form',
        'The chaconne must be in a minor key, whereas the passacaglia may be in either mode, which is why the two are catalogued separately in the Baroque repertory',
      ],
      0,
      'The distinction is loose and composers ignored it, but that is the conventional textbook division.',
    ],
    [
      'In gamelan music, what is a colotomic structure?',
      [
        'The pattern of punctuating gongs that marks cyclic form',
        'The tuning system of the metallophones',
        'The relationship between the two paired tunings',
        'The improvised elaboration of the core melody',
      ],
      0,
      'Instruments such as the kenong and kempul mark subdivisions of a gong cycle; the tunings are slendro and pelog.',
    ],
    [
      'Arnold Schoenberg twelve-tone method requires what of a row?',
      [
        'All twelve pitch classes appear before any repeats',
        'The row must be palindromic',
        'The row must avoid perfect fifths',
        'The row must begin and end on the same pitch',
      ],
      0,
      'The ordering is then subject to inversion, retrograde and retrograde inversion, plus transposition.',
    ],
    [
      'Which mode has the characteristic raised fourth degree?',
      ['Dorian', 'Phrygian', 'Lydian', 'Aeolian'],
      2,
      'Lydian is a major scale with a sharpened fourth. Dorian raises the sixth relative to natural minor.',
    ],
  ],
  professor: [
    [
      'What is hocket in medieval polyphony?',
      [
        'A melody distributed between voices in rapid alternation',
        'A drone held beneath a moving line',
        'A canon at the unison',
        'A cadential ornament applied to the penultimate note of a phrase',
      ],
      0,
      'Rests are interleaved so the line is shared between voices - characteristic of Ars Nova and of certain Central African practices.',
    ],
    [
      'In Schenkerian analysis, what is the Ursatz?',
      [
        'The fundamental structure of a descending melodic line over a bass arpeggiation',
        'The opening motif from which all others derive',
        'The original notated text as the composer left it, before any later editorial emendation',
        'The underlying rhythmic cell of a movement',
      ],
      0,
      'The Urlinie descends to the tonic over a I-V-I bass arpeggiation; everything else is a prolongation of it.',
    ],
    [
      'The Bohlen-Pierce scale divides which interval?',
      ['The octave', 'The tritave (a perfect twelfth)', 'The perfect fifth', 'The double octave, a ratio of four to one'],
      1,
      'Thirteen steps within a 3:1 frequency ratio, built for timbres whose partials are odd-numbered.',
    ],
    [
      'Which term describes the Renaissance practice of writing a mass on a pre-existing polyphonic model, borrowing all its voices?',
      ['Cantus firmus mass', 'Parody mass', 'Paraphrase mass', 'Canonic mass'],
      1,
      'Also called imitation mass. A cantus firmus mass borrows one line only; a paraphrase mass elaborates a monophonic source.',
    ],
    [
      'In Hindustani classical music, what is the vadi of a raga?',
      [
        'Its most prominent note',
        'Its rhythmic cycle',
        'Its opening improvised section',
        'Its ascending pitch sequence',
      ],
      0,
      'The vadi dominates and the samvadi answers it; the cycle is the tala, the opening section the alap, the ascent the aroha.',
    ],
  ],
  bscba: [
    [
      'What is a scale in music?',
      [
        'An ordered sequence of notes ascending or descending by step',
        'A set of variations on a single melodic theme',
        'The relative loudness of a musical passage',
        'The speed at which a piece is performed',
      ],
      0,
      'Major scale and natural minor are the most common in Western music, containing seven notes plus the octave.',
    ],
    [
      'What is harmony in music?',
      [
        'The sounding together of multiple notes at the same time',
        'The repeating pattern of strong and weak beats',
        'The overall shape and contour of a melody',
        'The rhythmic subdivision of musical time',
      ],
      0,
      'Harmony is created when notes with different pitches sound simultaneously, sung or played by different instruments.',
    ],
    [
      'A cadence in music is what?',
      [
        'A concluding progression of chords that ends a phrase or piece',
        'A sustained note held across a chord change',
        'A rapid ornamentation of a single note',
        'The opening theme of a movement',
      ],
      0,
      'Perfect cadence (V-I) and plagal cadence (IV-I) are most common. They provide harmonic closure.',
    ],
    [
      'What is a fugue?',
      [
        'A polyphonic composition with multiple independent melodic lines',
        'A fast, energetic section of a larger work',
        'A dramatic change in dynamics within a piece',
        'A technique of gradually slowing down the tempo',
      ],
      0,
      'A subject theme is introduced in one voice and imitated in others, creating complex counterpoint.',
    ],
    [
      'Instruments of the orchestra are traditionally divided into how many families?',
      [
        'Four: strings, woodwinds, brass and percussion',
        'Five: adding keyboards as a separate family',
        'Three: melody, harmony and rhythm',
        'Seven: adding historical classifications',
      ],
      0,
      'This Western system groups instruments by production method. Each family occupies a distinct register.',
    ],
    [
      'What is timbre in music?',
      [
        'The distinctive quality or color of a sound made by an instrument or voice',
        'The relative pitch between two notes',
        'The speed of vibration of a sound wave',
        'The duration of a single musical note',
      ],
      0,
      'Timbre is why a violin and trumpet sound different on the same note. It depends on overtones and production method.',
    ],
    [
      'A chord built on three notes is called:',
      [
        'A triad',
        'A dyad',
        'A seventh chord',
        'A suspended chord',
      ],
      0,
      'A triad has three pitches stacked in thirds. Major and minor triads are fundamental harmonic units.',
    ],
    [
      'What is polyphony in music?',
      [
        'Two or more independent melodic lines heard simultaneously',
        'A single melody with instrumental accompaniment',
        'The blending of similar instruments in unison',
        'The repetition of a musical phrase',
      ],
      0,
      'Each voice has its own melody that combines with others to create complex texture and harmony.',
    ],
    [
      'The Baroque period in Western music roughly spans which century?',
      [
        'The 17th century and early 18th century',
        'The 15th and 16th centuries',
        'The 19th century',
        'The 20th century',
      ],
      0,
      'Baroque music (roughly 1600-1750) featured ornate melody, harmonic innovation and forms like the concerto.',
    ],
    [
      'What is a sonata form?',
      [
        'A musical structure with exposition, development and recapitulation sections',
        'A piece written for a solo instrument with accompaniment',
        'A slow lyrical movement in a larger work',
        'A theme with a set number of variations',
      ],
      0,
      'Sonata form is basis of the first movement of symphonies and concertos. Drama comes from contrasting themes.',
    ],
    [
      'A diminished chord is built on what interval?',
      [
        'Stacked minor thirds',
        'Stacked major thirds',
        'Perfect fifths',
        'Major seconds',
      ],
      0,
      'Three notes separated by minor thirds produce diminished triad. It has tense, unstable sound that resolves.',
    ],
    [
      'The Renaissance period in music roughly spans which centuries?',
      [
        'The 15th and 16th centuries',
        'The 17th and 18th centuries',
        'The 13th and 14th centuries',
        'The 19th and 20th centuries',
      ],
      0,
      'Renaissance music (c. 1400-1600) gradually shifted from medieval modes toward modern major and minor scales.',
    ],
    [
      'What is an interval in music?',
      [
        'The distance in pitch between two notes',
        'The space between two lines on a musical staff',
        'A pause or rest during a performance',
        'The time signature of a piece',
      ],
      0,
      'Intervals are named by counting steps: a third spans three letters, a fifth spans five. Building blocks of melody.',
    ],
    [
      'What is counterpoint?',
      [
        'The art of combining independent melodic lines',
        'The accompaniment beneath a main melody',
        'The gradual thinning of musical texture',
        'The use of repeated rhythmic patterns',
      ],
      0,
      'Each line is musically independent while combining logically. Bach is the master of this technique.',
    ],
    [
      'What is a rondo form?',
      [
        'A structure in which a main theme alternates with contrasting sections',
        'A slow introduction followed by a fast movement',
        'A theme followed by a set of variations',
        'A piece that modulates through several keys',
      ],
      0,
      'Rondo form (ABACA...) uses memorable main theme that keeps returning. Common in finales of concertos.',
    ],
  ],
};

export const SCREEN: CategoryContent = {
  msc: [

    [
      'In French film criticism, what does decoupage denote?',
      [
        'The breakdown of a scene into shots, decided before shooting',
        'The final assembly of shots in the cutting room',
        'The lighting plan for a set, drawn from the shooting script',
        'The composition of elements within a single frame',
      ],
      0,
      'Decoupage is the plan; montage is the assembly. The distinction matters because it locates authorship before rather than after the shoot.',
    ],
    [
      'The Academy ratio is what aspect ratio?',
      ['1.37:1', '1.85:1', '2.35:1', '1.66:1'],
      0,
      'Standardised in 1932 for sound-on-film. 1.85 and 2.35 are the widescreen responses to television two decades later.',
    ],
    [
      'Bicycle Thieves was directed by whom?',
      [
        'Vittorio De Sica',
        'Roberto Rossellini, whose Rome, Open City preceded it by three years',
        'Luchino Visconti, whose La Terra Trema was released the same year',
        'Federico Fellini, then working as a screenwriter within the movement',
      ],
      0,
      'De Sica directed Bicycle Thieves in 1948, casting it largely with non-professionals in the neorealist manner. Fellini did in fact co-write the script, and Rossellini and Visconti were both active in the same movement in the same years, which is why the movement can seem like one collective project rather than the work of distinct directorial voices.',
    ],
    [
      'What does diegetic sound mean?',
      [
        'Sound whose source exists within the world of the story',
        'Sound recorded synchronously with the image on location',
        'Sound added in post-production to replace unusable location audio',
        'Sound heard by the audience but not by any character',
      ],
      0,
      'A radio a character switches on is diegetic; the score usually is not. The interesting cases are the ones that cross the boundary deliberately.',
    ],
    [
      'Continuity editing is principally designed to conceal what?',
      [
        'The cut itself, so that spatial and temporal relations feel unbroken',
        'The presence of the camera crew within the reflective surfaces of a set',
        'The compression of story time into screen time',
        'The use of doubles and stand-ins for principal actors',
      ],
      0,
      'Eyeline matches, the 180-degree rule and match-on-action all serve the same end: making an assembled space feel like a continuous one.',
    ],
  ],
  phd: [
    [
      'The Kuleshov effect demonstrates what?',
      [
        'That meaning arises from the juxtaposition of shots rather than their content',
        'That camera movement determines which character the audience ends up sympathising with',
        'That colour temperature alters perceived duration',
        'That sound can override visual continuity',
      ],
      0,
      'An identical shot of a face reads as grief or desire depending on the shot cut next to it.',
    ],
    [
      'What is a Dutch angle?',
      ['A canted camera', 'An extremely wide lens', 'A shot through a doorway', 'A reverse tracking shot'],
      0,
      'The frame is tilted off the horizontal. Nothing to do with the Netherlands - the term comes from Deutsch.',
    ],
    [
      'Which director is associated with the concept of the "cinema of poetry" set out in a 1965 essay?',
      ['Pier Paolo Pasolini', 'Michelangelo Antonioni', 'Jean-Luc Godard', 'Andrei Tarkovsky'],
      0,
      'Pasolini argued for a free indirect subjective, where the camera adopts a character way of seeing without ventriloquising them.',
    ],
    [
      'In classical Hollywood continuity editing, what does the 180-degree rule govern?',
      [
        'Keeping the camera on one side of the axis between two subjects',
        'The maximum permitted cut duration',
        'The angle change required between successive shots',
        'The direction of screen movement across a cut',
      ],
      0,
      'Crossing the axis flips left and right and disorients the viewer. The 30-degree rule governs angle change.',
    ],
    [
      'Which film movement issued the Dogme 95 manifesto?',
      ['The French New Wave', 'A Danish collective led by Lars von Trier and Thomas Vinterberg', 'New German Cinema', 'The Czech New Wave directors of the Barrandov studio generation'],
      1,
      'Ten "Vow of Chastity" rules: location shooting, no non-diegetic music, no credited director, among others.',
    ],
  ],
  professor: [
    [
      'What does Andre Bazin argue about deep focus in his writing on realism?',
      [
        'It preserves the ambiguity of the real by leaving the viewer free to choose where to look',
        'It heightens montage by increasing information per shot',
        'It flattens space and so abstracts the image',
        'It is a technical limitation of lens design rather than a deliberate aesthetic choice by the director',
      ],
      0,
      'Bazin set this against Soviet montage: composition in depth respects spatial continuity and hands interpretation to the spectator.',
    ],
    [
      'The term "phantom ride" refers to what in early cinema?',
      [
        'A film shot from the front of a moving vehicle',
        'A double exposure producing a ghost figure',
        'A projection using two synchronised machines',
        'A travelling exhibition of films in fairgrounds',
      ],
      0,
      'Popular from the late 1890s, usually shot from the cowcatcher of a train.',
    ],
    [
      'In Soviet montage theory, what did Eisenstein mean by "intellectual montage"?',
      [
        'Cutting that produces an abstract concept rather than a spatial or emotional relation',
        'Editing driven strictly by the rhythm of the musical accompaniment on the finished soundtrack',
        'Assembling documentary footage into an argument',
        'Matching graphic shapes across a cut',
      ],
      0,
      'The slaughterhouse intercut in Strike is the standard example: two concrete images yielding a third, abstract term.',
    ],
    [
      'What is the "acousmetre" in Michel Chion theory of film sound?',
      [
        'A voice heard but not seen, which holds power until it is localised',
        'The perceived spatial size of a recorded sound',
        'The synchronisation point between image and sound',
        'The residual noise of the recording apparatus audible beneath the take',
      ],
      0,
      'The mother voice in Psycho, or Mabuse. Chion argues it loses its power the moment it is given a body.',
    ],
    [
      'Which national cinema produced the "Golden Age" melodramas of the 1940s and 1950s associated with Emilio Fernandez and cinematographer Gabriel Figueroa?',
      ['Argentina', 'Mexico', 'Brazil', 'Egypt'],
      1,
      'Mexico. Figueroa deep-filtered skies and low horizons became the visual signature of the period.',
    ],
  ],
  bscba: [
    [
      'What is a film shot?',
      [
        'A single continuous recording from one camera position until the camera stops',
        'An individual photograph extracted from a sequence of film',
        'The entire sequence from the beginning to end of a scene',
        'The final edited version of a movie shown in theaters',
      ],
      0,
      'A shot is the fundamental building block of film editing. Multiple shots are combined to create scenes.',
    ],
    [
      'What does the term montage refer to in film?',
      [
        'The assembly and sequencing of shots in the editing process',
        'A dream sequence depicted through blurred imagery',
        'The superimposition of multiple images on one frame',
        'A rapid series of unrelated images showing the passage of time',
      ],
      0,
      'Montage is the art of film editing - how shots are combined to create meaning. The term comes from French.',
    ],
    [
      'What is the aspect ratio of a film?',
      [
        'The proportion of width to height of the projected image',
        'The frame rate measured in frames per second',
        'The length of a shot measured in seconds',
        'The angle at which the camera is positioned',
      ],
      0,
      'Standard ratios include 1.37:1 (Academy), 1.85:1 (widescreen) and 2.35:1 (anamorphic widescreen).',
    ],
    [
      'What is the establishing shot in film?',
      [
        'An initial wide shot that shows the location of a scene',
        'A close-up of a character\'s face at the start of a scene',
        'The shot that concludes a sequence',
        'A shot taken from a high angle looking down',
      ],
      0,
      'Establishing shot orients audience spatially. After this wide view, director can cut to closer shots.',
    ],
    [
      'What does the 180-degree rule refer to in filmmaking?',
      [
        'Keeping the camera on one side of an imaginary axis between characters',
        'The maximum distance a camera can be from the subject',
        'Rotating the camera 180 degrees for a reverse shot',
        'The angle of the sun for outdoor filming',
      ],
      0,
      'Crossing the axis flips left and right, disorienting viewer. It maintains spatial consistency across cuts.',
    ],
    [
      'What is a close-up shot?',
      [
        'A shot that frames a subject tightly, usually showing face or small object',
        'A shot taken from close to the action but showing full figures',
        'A shot taken from far away showing a wide landscape',
        'A shot taken at an extreme downward angle',
      ],
      0,
      'Close-ups focus emotional attention on character or object. Essential for conveying subtle expressions.',
    ],
    [
      'Film editing that maintains the illusion of continuous space and time is called:',
      [
        'Continuity editing',
        'Jump-cut editing',
        'Montage sequence editing',
        'Intercutting',
      ],
      0,
      'Continuity editing uses eyeline matches, match-on-action and 180-degree rule for seamless connection.',
    ],
    [
      'What is a pan shot in cinematography?',
      [
        'A camera movement that rotates horizontally from left to right or vice versa',
        'A shot showing the entire set or landscape',
        'A rapid cut from one location to another',
        'A shot in which the camera moves toward the subject',
      ],
      0,
      'A pan follows movement or reveals information. Differs from tracking shot, which moves through space.',
    ],
    [
      'What is the difference between a close-up and an extreme close-up?',
      [
        'An extreme close-up frames an even smaller area with less context',
        'A close-up is used for dialogue and extreme close-up for action',
        'Extreme close-up shows the full body whereas close-up shows the face',
        'There is no technical difference; terms are used interchangeably',
      ],
      0,
      'Extreme close-ups frame just eyes or lips, eliminating facial context. They create intensity.',
    ],
    [
      'What is depth of field in cinematography?',
      [
        'The range of distance in front of the lens that appears in focus',
        'The distance between the camera and the subject',
        'The angle of view captured by the camera lens',
        'The duration of time a shot remains on screen',
      ],
      0,
      'Shallow depth of field keeps subject in focus with blurred background. Deep depth keeps more scene sharp.',
    ],
    [
      'What is an overlay or superimposition in film?',
      [
        'Two images shown on screen at the same time, one partially transparent',
        'A voice heard while images from another scene are shown',
        'One image replacing another through a gradual transition',
        'Text or graphics added to an image',
      ],
      0,
      'Overlays show simultaneous events or memories. Ghost figures and double exposures are traditional techniques.',
    ],
    [
      'Cinematography refers to what aspect of filmmaking?',
      [
        'The art and technique of capturing images on film or digital media',
        'The writing of dialogue and narrative structure',
        'The arrangement and movement of actors on set',
        'The editing and assembly of shots',
      ],
      0,
      'Cinematography encompasses lighting, camera movement and lens choice. Cinematographer is director of photography.',
    ],
    [
      'What is a dolly shot?',
      [
        'A shot in which the camera physically moves toward or away from the subject',
        'A shot taken from a high vantage point above the scene',
        'A rapid cut between different camera angles',
        'A shot in which the camera rotates on a fixed axis',
      ],
      0,
      'Dolly is wheeled cart camera sits on. Dolly shots create motion sense and draw viewer into or out of space.',
    ],
    [
      'What is film noir?',
      [
        'A genre of crime films with dark, shadowy lighting and cynical characters',
        'A type of historical drama set in European cities',
        'A comedy that relies on physical humor and slapstick',
        'An abstract experimental film movement',
      ],
      0,
      'Film noir emerged in 1940s-50s with high-contrast black-and-white cinematography and morally ambiguous protagonists.',
    ],
    [
      'What is a reaction shot?',
      [
        'A close-up showing a character\'s emotional response to something said or seen',
        'A shot showing the entire action from beginning to end',
        'A camera movement that pans across the entire scene',
        'An overhead shot showing the layout of a location',
      ],
      0,
      'Reaction shots show listener\'s response, showing how they react. Essential for building emotional connection.',
    ],
  ],
};
